import React, { useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { FaArrowRight, FaHome } from 'react-icons/fa'
import { useAppDispatch, useAppSelector } from '../../../../hooks/useAppDispatch.js'
import { fetchIndustryBySlug } from '../../../../redux/slices/industriesSlice.js'
import SEO from '../../../../components/seo/SEO.jsx'
import { LazyImage } from '../../../../components/common/LazyImage.jsx'
import ErrorBlock from '../../../../components/common/ErrorBlock.jsx'
import SkeletonSection from '../../../../components/common/SkeletonSection.jsx'
import TestimonialsCarousel from '../../../../components/sections/TestimonialsCarousel.jsx'
import Cards from '../../../../components/common/Cards.jsx'
import { openModal } from '../../../../redux/slices/modalSlice.js';

import BuildSolution from '../../../../components/common/Common/BuildSolution/BuildSolution.jsx'


import './IndustryDetailPage.scss'

export default function IndustryDetailPage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { currentIndustry: industry, loading, error } = useAppSelector((state) => state.industries)

  const handleOpenContactModal = () => {
    dispatch(openModal({
      modalType: 'contact',
      modalProps: {}
    }));
  };

  useEffect(() => {
    if (slug) {
      dispatch(fetchIndustryBySlug(slug))
    }
  }, [dispatch, slug])

  if (loading) {
    return (
      <>
        <SEO title="Loading Industry..." />
        <div className="space-y-12">
          <SkeletonSection />
          <SkeletonSection />
          <SkeletonSection />
        </div>
      </>
    )
  }

  if (error || !industry) {
    return (
      <>
        <SEO title="Industry Not Found" />
        <div className="container-custom section-padding">
          <ErrorBlock
            title="Industry Not Found"
            message="The industry solution you're looking for doesn't exist or has been moved."
            onRetry={() => slug && dispatch(fetchIndustryBySlug(slug))}
          />
        </div>
      </>
    )
  }

  // v2 fields mapped into v1-style sections
  const hero = industry.hero || {}
  const solveChallenges = industry.solveChallenges || {}
  const whatWeDeliver = industry.whatWeDeliver || {}
  const impact = industry.impact || {}
  const techStack = industry.techStack || {}
  const stageProcess = industry.stageProcess || {}
  const cardSectionData = industry.cardsSection || {}

  const solveChallengeCards = Array.isArray(solveChallenges.list)
    ? solveChallenges.list.map((text, index) => ({
      id: index + 1,
      content: text
    }))
    : []

  const deliverCards = Array.isArray(whatWeDeliver.cards)
    ? whatWeDeliver.cards.map((card, index) => ({
      id: index + 1,
      image: card.imageUrl,
      heading: card.Heading || `Solution ${index + 1}`,
      para1: card.SubHeading || ''
    }))
    : []

  const impactCards = Array.isArray(impact.cards)
    ? impact.cards.map((card, index) => ({
      id: index + 1,
      image: card.imageUrl,
      description: card.title || ''
    }))
    : []

  const techCards = Array.isArray(techStack.cards)
    ? techStack.cards.map((card, index) => ({
      id: index + 1,
      image: card.imageUrl,
      text: card.title || `Tech ${index + 1}`
    }))
    : []

  const stages = Array.isArray(stageProcess.cards)
    ? stageProcess.cards.map((card, index) => ({
      number: index + 1,
      capsule: card.heading || `Stage ${index + 1}`,
      subheading: card.subheading || '',
      duration: card.duration || ''
    }))
    : []

  return (
    <>
      <SEO
        title={industry.seoTitle || industry.name}
        description={industry.seoDescription || `Digital solutions for ${industry.name}`}
        image={hero.imageUrl || industry.heroImageUrl}
        type="article"
      />

      <article className="industry-detail-page">
        {/* ================= HERO (v1 layout, v2 data) ================= */}
        {hero.isVisible !== false && (
          <section className="industry-hero-section-industries">
            <div className="industry-hero-container">
              {/* Breadcrumb */}
              <motion.div
                className="breadcrumb"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="breadcrumb-item" onClick={() => navigate('/')}>
                  <FaHome className="breadcrumb-icon" />
                  Home
                </span>
                <span className="breadcrumb-divider">/</span>
                <span className="breadcrumb-item" onClick={() => navigate('/industries')}>
                  Industries
                </span>
                <span className="breadcrumb-divider">/</span>
                <span className="breadcrumb-item active">
                  {hero.heading || industry.name}
                </span>
              </motion.div>

              {/* Heading */}
              <motion.h1
                className="industry-heading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(hero.heading || industry.name || '').toUpperCase()}
              </motion.h1>

              {/* Subheading */}
              <motion.p
                className="industry-subheading"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {hero.subheading ||
                  `Digital transformation solutions for ${industry.name}`}
              </motion.p>

              {/* Button */}
              {hero.buttonText && (
                <motion.button
                  onClick={() => {
                    handleOpenContactModal()
                  }}
                  className="industry-button"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  whileHover={{
                    scale: 1.05,
                    backgroundColor: 'var(--primary)',
                    color: '#000'
                  }}
                  whileTap={{ scale: 0.98 }}
                >
                  {hero.buttonText}
                  <FaArrowRight className="button-icon" />
                </motion.button>
              )}

              {/* Background card image */}
              {(hero.imageUrl || industry.heroImageUrl) && (
                <motion.div
                  className="card-hero"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.8 }}
                >
                  <div className="card-background">
                    <LazyImage
                      src={hero.imageUrl || industry.heroImageUrl}
                      alt={hero.heading || industry.name}
                      className="card-bg-image"
                      placeholder={<div className="card-bg-placeholder" />}
                    />
                    <div className="card-overlay"></div>
                  </div>
                </motion.div>
              )}
            </div>
          </section>
        )}

        {/* ================= CHALLENGES WE SOLVE (v1 ChallengesWeSolve) ================= */}
        {solveChallenges.isVisible !== false && solveChallengeCards.length > 0 && (
          <section className="content-section-ind">
            <div className="content-container">
              <div className="section-header">
                <motion.span
                  className="section-capsule"
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  CHALLENGES
                </motion.span>

                <motion.h2
                  className="section-heading"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  {(solveChallenges.heading || `Challenges We Solve in ${industry.name}`)
                    .toUpperCase()}
                </motion.h2>
              </div>

              <motion.div
                className="cards-grid"
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {/* First row */}
                <div className="grid-row">
                  {solveChallengeCards.slice(0, 2).map((card) => (
                    <motion.div
                      key={card.id}
                      className="content-card"
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: 0.1 * card.id }}
                      whileHover={{ y: -5 }}
                    >
                      <p className="card-content">{card.content}</p>
                    </motion.div>
                  ))}
                </div>

                {/* Second row */}
                {solveChallengeCards.length > 2 && (
                  <div className="grid-row">
                    {solveChallengeCards.slice(2, 4).map((card) => (
                      <motion.div
                        key={card.id}
                        className="content-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 * card.id }}
                        whileHover={{ y: -5 }}
                      >
                        <p className="card-content">{card.content}</p>
                      </motion.div>
                    ))}
                  </div>
                )}

                {/* Third row (single centered) */}
                {solveChallengeCards.length > 4 && (
                  <div className="grid-row single-card">
                    {solveChallengeCards.slice(4, 5).map((card) => (
                      <motion.div
                        key={card.id}
                        className="content-card"
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: 0.1 * card.id }}
                        whileHover={{ y: -5 }}
                      >
                        <p className="card-content">{card.content}</p>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </section>
        )}

        {/* ================= OUR SOLUTIONS / WHAT WE DELIVER (v1 OurSolutions) ================= */}
        {whatWeDeliver.isVisible !== false && deliverCards.length > 0 && (
          <section className="vertical-cards-section-indu">
            <div className="vertical-cards-container">
              <motion.div
                className="centered-capsule"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                WHAT WE DELIVER
              </motion.div>

              <motion.h2
                className="section-heading"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(whatWeDeliver.Heading || `Solutions for ${industry.name}`).toUpperCase()}
              </motion.h2>

              <div className="vertical-cards">
                {deliverCards.map((card, index) => (
                  <motion.div
                    key={card.id}
                    className="process-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.2 }}
                  >
                    <div className="card-image">
                      {card.image ? (
                        <LazyImage
                          src={card.image}
                          alt={card.heading}
                          placeholder={<div className="card-image-placeholder" />}
                        />
                      ) : (
                        <div className="card-image-placeholder" />
                      )}
                      <div className="image-overlay"></div>
                    </div>

                    <div className="card-content">
                      <h3 className="card-heading">
                        <span className="heading-number">0{card.id}.</span>
                        {card.heading}
                      </h3>
                      {card.para1 && <p className="card-para">{card.para1}</p>}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}

        {/* ================= RESULTS / IMPACT (v1 ResultsDelivered) ================= */}
        {impact.isVisible !== false && impactCards.length > 0 && (
          <section className="results-delivered-section-ind">
            <div className="results-container">
              <motion.div
                className="centered-capsule"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                Impact
              </motion.div>

              <motion.h2
                className="section-heading"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(impact.Heading || "Results We've Delivered").toUpperCase()}
              </motion.h2>

              <div className="results-grid">
                <motion.div
                  className="results-row"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                >
                  {impactCards.slice(0, 2).map((card) => (
                    <motion.div
                      key={card.id}
                      className="result-card"
                      whileHover={{ y: -5 }}
                      transition={{ duration: 0.3 }}
                    >
                      <div className="card-image-row">
                        {card.image && (
                          <LazyImage
                            src={card.image}
                            alt={card.description}
                            className="result-icon"
                            placeholder={<div className="result-icon-placeholder" />}
                          />
                        )}
                      </div>
                      <div className="card-content-row">
                        <p className="result-description">{card.description}</p>
                      </div>
                    </motion.div>
                  ))}
                </motion.div>

                {impactCards.length > 2 && (
                  <motion.div
                    className="results-row"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: 0.6 }}
                  >
                    {impactCards.slice(2, 4).map((card) => (
                      <motion.div
                        key={card.id}
                        className="result-card"
                        whileHover={{ y: -5 }}
                        transition={{ duration: 0.3 }}
                      >
                        <div className="card-image-row">
                          {card.image && (
                            <LazyImage
                              src={card.image}
                              alt={card.description}
                              className="result-icon"
                              placeholder={<div className="result-icon-placeholder" />}
                            />
                          )}
                        </div>
                        <div className="card-content-row">
                          <p className="result-description">{card.description}</p>
                        </div>
                      </motion.div>
                    ))}
                  </motion.div>
                )}
              </div>
            </div>
          </section>
        )}

        {/* ================= TECH STACK (v1 TechStack) ================= */}
        {techStack.isVisible !== false && techCards.length > 0 && (
          <section className="icon-features-section-ind">
            <div className="features-container">
              <motion.div
                className="centered-capsule"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
              >
                TECH STACK
              </motion.div>

              <motion.h2
                className="section-heading"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(techStack.Heading || 'Our Tech Stack').toUpperCase()}
              </motion.h2>

              <motion.div
                className="features-grid"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                {techCards.map((card, index) => (
                  <motion.div
                    key={card.id}
                    className="feature-item"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ scale: 1.05 }}
                  >
                    <div className="feature-icon">
                      {card.image && (
                        <LazyImage
                          src={card.image}
                          alt={card.text}
                          className="tech-icon-image"
                          placeholder={<div className="tech-icon-placeholder" />}
                        />
                      )}
                    </div>
                    <span className="feature-text">{card.text}</span>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>
        )}

        {/* ================= STAGE PROCESS (v1 StageProcess) ================= */}
        {stageProcess.isVisible !== false && stages.length > 0 && (
          <section className="industry-stages-section">
            <div className="stages-container">
              <motion.button
                className="stages-button"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                OUR PROCESS
              </motion.button>

              <motion.h2
                className="stages-heading"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {(stageProcess.Heading || 'Project Development Stages').toUpperCase()}
              </motion.h2>

              <div className="stages-timeline">
                <div className="timeline-line"></div>

                {stages.map((stage, index) => (
                  <motion.div
                    key={index}
                    className="stage-card"
                    initial={{ opacity: 0, x: -30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                  >
                    <div className="timeline-number">
                      <span className="number">
                        {stage.number < 10 ? `0${stage.number}` : stage.number}
                      </span>
                    </div>

                    <div className="card-content">
                      <span className="stage-capsule">{stage.capsule}</span>
                      <h3 className="stage-subheading">{stage.subheading}</h3>
                      {stage.duration && (
                        <span className="stage-duration">{stage.duration}</span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
      <TestimonialsCarousel />
      <BuildSolution />
      <section className="service-cards-section">
        <div className="service-cards-container">
          {cardSectionData.capsuleText && (
            <motion.div
              className="centered-capsule"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              {cardSectionData.capsuleText || "Why Choose Us?"}
            </motion.div>
          )}

          <motion.h2
            className="section-heading"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {cardSectionData.Heading?.toUpperCase()}
          </motion.h2>
          <Cards />

        </div>
      </section>
      {/* <Cards /> */}
    </>
  )
}
